import { PartnerOnesheet } from '@provision/entity-types';
import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { NullableDeepPartial } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { PARTNER_ONESHEET_FIELDS } from './partner';

const CREATE_PARTNER_ONESHEET_MUTATION = gql`
  mutation CreatePartnerOnesheet($data: PartnerOnesheetDataInput) {
    createPartnerOnesheet(data: $data) {
      ...PartnerOneSheetFields
    }
  }
  ${PARTNER_ONESHEET_FIELDS}
`;

const UPDATE_PARTNER_ONESHEET_MUTATION = gql`
  mutation UpdatePartnerOnesheet(
    $conditions: [ConditionInput]
    $data: PartnerOnesheetDataInput
  ) {
    updatePartnerOnesheet(conditions: $conditions, data: $data) {
      ...PartnerOneSheetFields
    }
  }
  ${PARTNER_ONESHEET_FIELDS}
`;

export interface CreatePartnerOnesheet {
  createPartnerOnesheet: NullableDeepPartial<PartnerOnesheet>[];
}

export interface UpdatePartnerOnesheet {
  updatePartnerOnesheet: NullableDeepPartial<PartnerOnesheet>[];
}

export const usePartnerOnesheet = (partnerId?: number | null) => {
  const { showErrorAlert } = useNotifications();

  const [updatePartnerOnesheetMutation] = useMutation<UpdatePartnerOnesheet>(
    UPDATE_PARTNER_ONESHEET_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating partner, please try again');
      },
    },
  );

  const updateWithOptimisticResponse = useCallback(
    (patch: Partial<PartnerOnesheet>, partnerOnesheet: NullableDeepPartial<PartnerOnesheet>) => {
      if (partnerId === null || partnerId === undefined) return Promise.resolve();

      return updatePartnerOnesheetMutation({
        variables: {
          conditions: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: partnerId.toString(),
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updatePartnerOnesheet: [
            {
              ...partnerOnesheet,
              ...patch,
            },
          ],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updatePartnerOnesheet?.[0];
          if (!updated) return;

          const id = cache.identify({
            __typename: 'Partner',
            id: partnerId,
          });

          cache.modify({
            id,
            fields: {
              partnerOnesheet(_, { toReference }) {
                return [
                  toReference({
                    ...partnerOnesheet,
                    ...updated,
                  }),
                ];
              },
            },
          });
        },
      });
    },
    [partnerId, updatePartnerOnesheetMutation],
  );

  const [createPartnerOnesheetMutation] = useMutation<CreatePartnerOnesheet>(
    CREATE_PARTNER_ONESHEET_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating partner, please try again');
      },
    },
  );

  const createWithOptimisticResponse = useCallback(
    (patch: Partial<PartnerOnesheet>, partnerOnesheet: NullableDeepPartial<PartnerOnesheet>) => {
      if (partnerId === null) return Promise.resolve();

      return createPartnerOnesheetMutation({
        variables: {
          data: patch,
        },
        optimisticResponse: {
          createPartnerOnesheet: [
            {
              ...partnerOnesheet,
              ...patch,
            },
          ],
        },
        update: (cache, { data: response }) => {
          const created = response?.createPartnerOnesheet?.[0];
          if (!created) return;

          const id = cache.identify({
            __typename: 'Partner',
            id: partnerId,
          });

          cache.modify({
            id,
            fields: {
              partnerOnesheet(_, { toReference }) {
                return [
                  toReference(
                    {
                      ...partnerOnesheet,
                      ...created,
                    },
                    true,
                  ),
                ];
              },
            },
          });
        },
      });
    },
    [createPartnerOnesheetMutation, partnerId],
  );

  return {
    updatePartnerOnesheet: updateWithOptimisticResponse,
    createPartnerOneSheet: createWithOptimisticResponse,
  };
};
